import React from 'react';

import Modal from '@hero/ui-kit/components/Modal';
import Button from '@hero/ui-kit/inputs/Button';
import Section from '@hero/ui-kit/layout/Section';
import H from '@hero/ui-kit/typography/H';
import P from '@hero/ui-kit/typography/P';
import useBulkAssignCareManagersCsv from '../../api/useBulkAssignCareManagersCsv';
import { translateBulkAssignCsvError } from './errors';
import * as Style from './style.module.scss';

interface BulkAssignCsvModalProps {
    externalControls: [boolean, (state: boolean) => void];
}

const CSV_EXAMPLE = `external_user_id,care_manager_email
EXT001,jane@example.com
EXT002,jane@example.com
EXT003,john@example.com`;

const BulkAssignCsvModal: React.FC<BulkAssignCsvModalProps> = ({ externalControls }) => {
    const [, setOpen] = externalControls;
    const inputRef = React.useRef<HTMLInputElement | null>(null);
    const [selectedFile, setSelectedFile] = React.useState<File | null>(null);
    const [errorMessages, setErrorMessages] = React.useState<string[]>([]);

    const handleClose = React.useCallback(() => {
        setOpen(false);
        setSelectedFile(null);
        setErrorMessages([]);
    }, [setOpen]);

    const { mutateAsync, isPending } = useBulkAssignCareManagersCsv(handleClose);

    const handleFileChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            setSelectedFile(file);
            setErrorMessages([]);
        }
        e.target.value = '';
    }, []);

    const handleUpload = React.useCallback(() => {
        if (!selectedFile) return;

        mutateAsync(selectedFile).catch((error) => {
            const rawErrors: string[] =
                error?.errors || error?.details?.errors || [];
            setErrorMessages(rawErrors.map(translateBulkAssignCsvError));
        });
    }, [selectedFile, mutateAsync]);

    return (
        <Modal
            externalControls={externalControls}
            isCancelable
            onClose={handleClose}
            styles={{ minWidth: '54rem', padding: 0 }}
        >
            <div className={Style.content}>
                <Section noDefaultPadding centered>
                    <H role="h5">Bulk Assign Care Managers via CSV</H>
                </Section>

                <P>
                    Upload a CSV file to assign Care Managers to members in bulk. Each row maps a
                    member (by <strong>external_user_id</strong>) to a care manager (by{' '}
                    <strong>care_manager_email</strong>).
                </P>

                <P strong>CSV Format:</P>
                <div className={Style.csvExample}>
                    <pre>{CSV_EXAMPLE}</pre>
                </div>

                <div className={Style.uploadArea}>
                    <input
                        ref={inputRef}
                        type="file"
                        accept=".csv"
                        onChange={handleFileChange}
                        style={{ display: 'none' }}
                    />
                    {selectedFile ? (
                        <>
                            <strong>{selectedFile.name}</strong>
                            <Button
                                variant="secondary"
                                noDefaultMargin
                                onClick={() => inputRef.current?.click()}
                            >
                                Choose different file
                            </Button>
                        </>
                    ) : (
                        <Button
                            variant="secondary"
                            noDefaultMargin
                            onClick={() => inputRef.current?.click()}
                        >
                            Select CSV file
                        </Button>
                    )}
                </div>

                {errorMessages.length > 0 && (
                    <div className={Style.error}>
                        {errorMessages.length === 1 ? (
                            errorMessages[0]
                        ) : (
                            <ul className={Style.errorList}>
                                {errorMessages.map((message, i) => (
                                    <li key={i}>{message}</li>
                                ))}
                            </ul>
                        )}
                    </div>
                )}

                <div className={Style.actions}>
                    <Button variant="secondary" noDefaultMargin onClick={handleClose}>
                        Cancel
                    </Button>
                    <Button
                        noDefaultMargin
                        onClick={handleUpload}
                        disabled={!selectedFile || isPending}
                    >
                        {isPending ? 'Uploading...' : 'Upload & Assign'}
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

export default BulkAssignCsvModal;
